import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Select, MenuItem, Paper, TextField, Checkbox, ListItemText, Grid } from "@mui/material";
import { Pie } from 'react-chartjs-2';
import { Chart, ArcElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import { useDiscreteMode } from "../../global/DiscreteMode";
import { useGetDataAssetByNameQuery } from "../../../../features/data_assets/dataAssetsApiSlice";
import { DataGrid } from '@mui/x-data-grid'; // or from '@material-ui/data-grid' based on your version
import { DateRangePicker } from '@mui/x-date-pickers-pro'


import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // For date-fns


Chart.register(ArcElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const predefinedRanges = {
  '1d': { label: 'Last 1 Day', days: 1 },
  '7d': { label: 'Last 7 Days', days: 7 },
  '30d': { label: 'Last 30 Days', days: 30 },
  'all': { label: 'All Time' }
};

const getFilteredRowsByDateRange = (rows, rangeKey) => {
  if (rangeKey === 'all') return rows;
  const range = predefinedRanges[rangeKey];
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - range.days + 1); // +1 to include the start day in the range
  return rows.filter(row => {
    const rowDate = new Date(row.date);
    return rowDate >= startDate && rowDate <= endDate;
  });
};



const getChartData = (data, type, range, isDiscreteMode) => {
  const labels = [];
  const dataset = [];
  let summary = 0;

  if (!data || !data.contributionMetrics) return { labels, datasets: [{ data: dataset }] };

  data.contributionMetrics.forEach((entry) => {
    if (entry.metrics && entry.metrics[range]) {
      entry.metrics[range].forEach((metric) => {
        const label = isDiscreteMode ? "Hidden" : entry.contributor;

        if (range === 'summary') {
          // Sum all metric values in summary array
          labels.push(label);
          dataset.push(metric[type]);
          summary += metric[type];
        } else if (metric.name === "Total") {
          // Process Total metrics for sevenDay and thirtyDay
          labels.push(label);
          dataset.push(metric[type]);
          summary += metric[type];
        }
      });
    }
  });

  return {
    labels,
    datasets: [
      {
        data: dataset,
        backgroundColor: ['#A8E6CF', '#DCEDC1', '#FFD3B6', '#FFAAA5', '#FF8B94'],
        label: `${type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} (${range})`,
      },
    ],
    summary,
  };
};

const ContributionMetrics = () => {
  const [chartType, setChartType] = useState("unique_pairs");
  const [range, setRange] = useState("sevenDay");
  const [selectedRange, setSelectedRange] = useState('7d');

  const [organizationNames, setOrganizationNames] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);


  const { isDiscreteMode } = useDiscreteMode();
  const { data: cookiePoolData, isLoading: isLoadingCookiePool } = useGetDataAssetByNameQuery("ADC Cookie Pool");

  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [showChart, setShowChart] = useState(false); // Initially false to hide the chart




  const chartData = getChartData(cookiePoolData, chartType, range, isDiscreteMode);

  const columns = [
    { field: 'date', headerName: 'Date', width: 100 },
    { field: 'memberName', headerName: 'Member Name', width: 180 },
    // Existing "Net New" and "Net Provided" columns
    { field: 'unique_pairs', headerName: 'Net New Unique Pairs', width: 130 },
    { field: 'unique_hems', headerName: 'Net New Unique HEMs', width: 130 },
    { field: 'unique_cookies', headerName: 'Net New Unique Cookies', width: 140 },
    { field: 'net_provided_unique_pairs', headerName: 'Total Provided Unique Pairs', width: 150 },
    { field: 'net_provided_unique_hems', headerName: 'Total Provided Unique HEMs', width: 150 },
    { field: 'net_provided_unique_cookies', headerName: 'Total Provided Unique Cookies', width: 160 },
    // New column for source type descriptions
    { field: 'source_type_desc', headerName: 'Source Type', width: 200 },
  ];

  const rows = cookiePoolData?.contributionStats?.map((stat, index) => ({
    id: index,
    date: stat.date.split('T')[0], // Splits the ISO string and takes only the date part
    memberName: stat.memberName,
    // Existing stats
    unique_pairs: stat.unique_pairs,
    unique_hems: stat.unique_hems,
    unique_cookies: stat.unique_cookies,
    // Placeholder values for "Net Provided" stats
    net_provided_unique_pairs: stat.net_provided_unique_pairs || 'N/A', // Use actual data if available
    net_provided_unique_hems: stat.net_provided_unique_hems || 'N/A', // Use actual data if available
    net_provided_unique_cookies: stat.net_provided_unique_cookies || 'N/A', // Use actual data if available
    // Convert source_type to descriptive text
    source_type_desc: stat.source_type ? convertSourceTypeToDesc(stat.source_type) : 'N/A',
  })) || [];
  
  // Helper function to convert source_type values to descriptive text
  function convertSourceTypeToDesc(sourceType) {
    switch (sourceType) {
      case 'S3':
        return 'S3 Contribution';
      case 'av':
        return 'Audience Validation';
      case 'redirect':
        return 'Redirect Cookie Sync';
      case 'tag_input':
        return 'Tag Fire HEM';
      default:
        return 'N/A'; // Default case if none of the expected values match
    }
  }

  useEffect(() => {
    const uniqueNames = [...new Set(rows.map(row => row.memberName))];
    setOrganizationNames(uniqueNames);
  }, [rows]);

  if (isLoadingCookiePool) {
    return <div>Loading...</div>; // Show a loading state while data is being fetched
  }

  const filteredRows = getFilteredRowsByDateRange(rows, selectedRange).filter(row => {
    return selectedOrganizations.length === 0 || selectedOrganizations.includes(row.memberName);
  });




  const handleChangeChartType = (event) => {
    setChartType(event.target.value);
  };

  const handleChangeRange = (event) => {
    setRange(event.target.value);
  };


  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  // Calculate the stat for the selected organization
  const calculateStatForSelectedOrganization = () => {
    if (!selectedRow) return { daysWithTotalPairsOverZero: 0 };

    const daysWithTotalPairsOverZero = rows.filter(row => row.memberName === selectedRow.memberName && row.unique_pairs > 0).length;

    return { daysWithTotalPairsOverZero };
  };

  const { daysWithTotalPairsOverZero } = calculateStatForSelectedOrganization();

  const options = {
    responsive: true,
    maintainAspectRatio: true, // Ensure the aspect ratio is maintained
    aspectRatio: 1, // Adjust the aspect ratio as needed to scale down
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          color: 'white',
          generateLabels: function (chart) {
            const labels = Chart.overrides.pie.plugins.legend.labels.generateLabels.call(this, chart);
            return labels.map((label) => {
              const value = chartData.datasets[0].data[label.index].toLocaleString();
              return {
                ...label,
                text: `${label.text}: ${value}`,
                font: { size: 10 },
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            let label = chartData.labels[tooltipItem.dataIndex];
            let value = chartData.datasets[tooltipItem.datasetIndex].data[tooltipItem.dataIndex].toLocaleString();
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Predefined range selector */}
      <Select
        value={selectedRange}
        onChange={(event) => setSelectedRange(event.target.value)}
        sx={{ backgroundColor: '#757575', marginTop: 1, marginBottom: 1, width: '100%' }}
      >
        {Object.entries(predefinedRanges).map(([key, { label }]) => (
          <MenuItem key={key} value={key}>
            {label}
          </MenuItem>
        ))}
      </Select>

      {/* Organization selection to filter rows based on selected organizations */}
      <Select
        multiple
        value={selectedOrganizations}
        onChange={(event) => setSelectedOrganizations(event.target.value)}
        renderValue={(selected) => selected.join(', ')}
        sx={{ backgroundColor: '#757575', marginTop: 1, marginBottom: 1, width: '100%' }}
      >
        {organizationNames.map((name) => (
          <MenuItem key={name} value={name}>
            <Checkbox checked={selectedOrganizations.indexOf(name) > -1} />
            <ListItemText primary={name} />
          </MenuItem>
        ))}
      </Select>

      {/* Button to toggle the visibility of the chart section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: 1 }}>
        <Button
          variant="contained"
          onClick={() => setShowChart(!showChart)}
          sx={{ mb: 2 }}
        >
          {showChart ? 'Hide Chart' : 'Show Chart'}
        </Button>
      </Box>

      {/* Conditional rendering for the pie chart section based on showChart state */}
      {showChart && (
  <Box sx={{ width: '100%', maxWidth: '600px', margin: 'auto', backgroundColor: '#3F4957', padding: 2, borderRadius: 1, marginBottom: 2 }}>
          <Select value={chartType} onChange={handleChangeChartType} sx={{ backgroundColor: '#757575', marginTop: 1, marginBottom: 1 }}>
            <MenuItem value="unique_pairs">Unique Pairs</MenuItem>
            <MenuItem value="unique_hems">Unique HEMs</MenuItem>
            <MenuItem value="unique_cookies">Unique Cookies</MenuItem>
          </Select>
          <Select value={range} onChange={handleChangeRange} sx={{ backgroundColor: '#757575', marginTop: 1, marginLeft: 2, marginBottom: 1 }}>
            <MenuItem value="summary">Summary</MenuItem>
            <MenuItem value="sevenDay">Last 7 Days</MenuItem>
            <MenuItem value="thirtyDay">Last 30 Days</MenuItem>
          </Select>
          <Pie data={chartData} options={options} />
        </Box>
      )}

      {/* DataGrid to display filtered rows */}
      <Paper sx={{ height: 'auto', width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={20}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
          onRowClick={handleRowClick} // Add this prop to handle row clicks
          sx={{ height: 700, width: '100%' }}
        />
      </Paper>
      {selectedRow && (
        <Paper elevation={3} sx={{ mt: 2, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Organization Profile
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography>Member Name: {selectedRow.memberName}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>Days with Total Pairs Over Zero: {daysWithTotalPairsOverZero}</Typography>
            </Grid>
            {/* Add more stats as needed */}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default ContributionMetrics;